import DesignButton from 'components/button/DesignButton';
import DesignCard from 'components/card/DesignCard';
import { useCapacityPlanDeliveryIssuesQuery } from 'core/graphql/CapacityPlanning/CapacityPlanDeliveryIssues.generated';
import { CapacityPlanRunDetail } from 'core/graphql/CapacityPlanning/models';
import {
  CapacityPlanDeliveryFilter,
  CapacityPlanDeliveryIssueKind,
} from 'core/graphql/types';
import GeocodeButton from './GeocodeButton';
import useCapacityPlanActions from './useCapacityPlanActions';

export interface DeliveryIssuesCardProps {
  run: CapacityPlanRunDetail;
  canEdit: boolean;
  /** Applies the filter chip matching the group the supervisor clicked. */
  onShow: (filter: CapacityPlanDeliveryFilter) => void;
}

/** The chip a group jumps to, so "Show" narrows the table to that group. */
export const filterForKind = (
  kind: CapacityPlanDeliveryIssueKind,
): CapacityPlanDeliveryFilter => {
  switch (kind) {
    case CapacityPlanDeliveryIssueKind.InvalidRow:
      return CapacityPlanDeliveryFilter.Invalid;
    case CapacityPlanDeliveryIssueKind.FailedGeocode:
      return CapacityPlanDeliveryFilter.FailedGeocode;
    case CapacityPlanDeliveryIssueKind.OutOfCountry:
      return CapacityPlanDeliveryFilter.OutOfCountry;
    default:
      return CapacityPlanDeliveryFilter.Approximated;
  }
};

const dotClasses: { [key in CapacityPlanDeliveryIssueKind]: string } = {
  [CapacityPlanDeliveryIssueKind.InvalidRow]: 'bg-darker-red',
  [CapacityPlanDeliveryIssueKind.FailedGeocode]: 'bg-darker-red',
  [CapacityPlanDeliveryIssueKind.OutOfCountry]: 'bg-darker-red',
  [CapacityPlanDeliveryIssueKind.Approximated]: 'bg-yellow',
};

/**
 * The reasons the supervisor has to look at, grouped by the server.
 */
const DeliveryIssuesCard = ({
  run,
  canEdit,
  onShow,
}: DeliveryIssuesCardProps) => {
  const { data } = useCapacityPlanDeliveryIssuesQuery({
    variables: { runId: run.id },
  });
  const { requestGeocoding, isRequestingGeocoding } = useCapacityPlanActions(
    run.id,
  );

  const issues = data?.capacityPlanDeliveryIssues ?? [];
  if (issues.length === 0) {
    return null;
  }

  return (
    <DesignCard title="Issues">
      <ul className="flex flex-col gap-2">
        {issues.map((issue) => (
          <li
            key={`${issue.kind}-${issue.reason}`}
            className="flex items-center gap-4 text-sm"
          >
            <span
              aria-hidden="true"
              className={`h-2 w-2 shrink-0 rounded-full ${
                dotClasses[issue.kind]
              }`}
            />
            <span className="flex-grow">{issue.reason}</span>
            <span className="rounded-sm bg-muted-white px-1.5 text-xs dark:bg-design-black-accent">
              {issue.count}
            </span>
            <DesignButton
              variant="ghost"
              size="sm"
              ariaLabel={`Show ${issue.reason}`}
              onClickHandler={() => onShow(filterForKind(issue.kind))}
            >
              Show
            </DesignButton>
          </li>
        ))}
      </ul>
      <div className="mt-3 flex items-center gap-3">
        <p className="flex-grow text-xs text-light-black dark:text-sublte-white">
          Rows without coordinates can be sent to address processing, or given
          coordinates by hand in the table.
        </p>
        <GeocodeButton
          status={run.status}
          missingCoordinates={run.missingCoordinates}
          canEdit={canEdit}
          isRequesting={isRequestingGeocoding}
          onConfirm={requestGeocoding}
          size="sm"
        />
      </div>
    </DesignCard>
  );
};

export default DeliveryIssuesCard;
